import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import ButtonLink from '@/common/components/ButtonLink'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters'
import { ContractProductCategoriesProductItem } from '@/features/privateCompanies/components/showContract/CreditLimits/ContractProductCategoriesProductItem'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { BoxesIcon, FileBoxIcon } from 'lucide-react'

interface ContractProductCategoryItemProps {
  contractProductCategory: CompanyContractType['contract_product_categories'][number]
  contractProductCategoryIndex: number
  contract?: CompanyContractType
}

export const ContractProductCategoryItem = ({
  contractProductCategory,
  contractProductCategoryIndex,
  contract,
}: ContractProductCategoryItemProps) => {
  return (
    <Box className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="border-s border-sky-300 px-3 py-2 flex">
          <ButtonLink
            linkTo={`/product-categories/${contractProductCategory?.product_category?.id}`}
            operationKlassToCheckPermission='Admin::ProductCategories::Show'
            className='underline-offset-2'
            dataTestId='product_category_clickable_link'
          >
            <Badge
              valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_display_name`}
              Icon={BoxesIcon}
              iconProps={{ className: 'size-5', strokeWidth: 1 }}
              text={contractProductCategory?.product_category?.display_name}
              overwriteClassName
            />
          </ButtonLink>
        </div>
      </div>

      {(contractProductCategory.global_value_in_currency ||
        contractProductCategory.global_value_in_unit_for_consumption) && (
          <div className="flex gap-4">
            {contract?.consumption_controlled_by !== 'quantity' && (
              <ResumeItem
                valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_global_value_in_currency`}
                label="Cota total em valor"
                value={
                  contractProductCategory.global_value_in_currency
                    ? formatCurrency(Number(contractProductCategory.global_value_in_currency))
                    : ''
                }
                labelClassName="text-white-400"
                valueClassName="text-md"
              />
            )}

            <ResumeItem
              valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_global_value_in_unit_for_consumption`}
              label="Cota total em quantidade"
              value={
                contractProductCategory.global_value_in_unit_for_consumption
                  ? formatUnit(
                    formatDecimal(Number(contractProductCategory.global_value_in_unit_for_consumption), 0),
                    'item',
                  )
                  : ''
              }
              labelClassName="text-white-400"
              valueClassName="text-md"
            />
          </div>
        )}

      {!!contractProductCategory.contract_product_category_products?.length && (
        <div className="flex flex-col gap-4">
          {!(
            contractProductCategory.global_value_in_currency ||
            contractProductCategory.global_value_in_unit_for_consumption ||
            contractProductCategory.contract_product_category_products.some((product) => product.price_rule === 'fixed')
          ) ? (
            <Box className="p-4 flex gap-3 flex-wrap">
              {contractProductCategory.contract_product_category_products?.map(
                (contractProductCategoryProduct, index) => (
                  <ButtonLink
                    key={contractProductCategoryProduct.id}
                    linkTo={`/products/${contractProductCategoryProduct?.product?.id}`}
                    operationKlassToCheckPermission='Admin::Products::Show'
                    className='underline-offset-2'
                    dataTestId='product_clickable_link'
                  >
                    <Badge
                      valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${index}_display_name`}
                      Icon={FileBoxIcon}
                      iconProps={{ className: 'size-5', strokeWidth: 1 }}
                      text={contractProductCategoryProduct?.product?.display_name}
                      className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
                      overwriteClassName
                      contentClassName="px-3 py-2"
                    />
                  </ButtonLink>
                ),
              )}
            </Box>
          ) : (
            contractProductCategory.contract_product_category_products?.map((contractProductCategoryProduct, index) => (
              <ContractProductCategoriesProductItem
                key={contractProductCategoryProduct.id}
                contractProductCategoryProduct={contractProductCategoryProduct}
                contractProductCategoryIndex={contractProductCategoryIndex}
                contractProductCategoryProductIndex={index}
                contract={contract}
              />
            ))
          )}
        </div>
      )}
    </Box>
  )
}
